/**
 * Dynamic password validation based on policy.
 * @param {string} password - The password to validate.
 * @param {object} policy - The policy object from backend.
 * @returns {object} - Validation results { score, strength, requirements, isValid, errorMessage }
 */

// Global default (matches backend global defaults exactly)
const DEFAULT_POLICY = {
  min_length: 8,
  require_uppercase: true,
  require_lowercase: true,
  require_number: true,
  require_special_char: true,
};

export const validatePassword = (password, policy) => {
  const safePolicy = policy || {};
  const p = {
    min_length: safePolicy.min_length ?? safePolicy.password_min_length ?? DEFAULT_POLICY.min_length,
    require_uppercase: !!(safePolicy.require_uppercase ?? safePolicy.requires_uppercase ?? safePolicy.password_requires_uppercase ?? DEFAULT_POLICY.require_uppercase),
    require_lowercase: !!(safePolicy.require_lowercase ?? safePolicy.requires_lowercase ?? safePolicy.password_requires_lowercase ?? DEFAULT_POLICY.require_lowercase),
    require_number: !!(safePolicy.require_number ?? safePolicy.requires_number ?? safePolicy.password_requires_number ?? DEFAULT_POLICY.require_number),
    require_special_char: !!(safePolicy.require_special_char ?? safePolicy.requires_special ?? safePolicy.password_requires_special ?? DEFAULT_POLICY.require_special_char),
  };

  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  // Build requirements list — only requirements flagged 'active' (required by policy) are checked.
  // If a requirement is NOT required by policy → auto-satisfied (true). 
  // CRITICAL: not requiring X does NOT mean X is forbidden. Users can always add extra chars.
  const requirements = [
    {
      label: `At least ${p.min_length} characters`,
      met: password.length >= p.min_length,
      active: true,
    },
    {
      label: "Number",
      met: hasNumber,
      active: p.require_number,
    },
    {
      label: "Uppercase letter",
      met: hasUpper,
      active: p.require_uppercase,
    },
    {
      label: "Lowercase letter",
      met: hasLower,
      active: p.require_lowercase,
    },
    {
      label: "Special character",
      met: hasSpecial,
      active: p.require_special_char,
    },
  ];

  // Only active (required) constraints that are not met cause a validation failure
  const requirementViolations = requirements.filter(r => r.active && !r.met).map(r => r.label);
  const isValid = requirementViolations.length === 0;

  // --- DEBUG LOG ---
  // console.group('%c[Password Validation Check]', 'color: #10b981; font-weight: bold; font-size: 12px;');
  // console.log('Password     :', password ? '●'.repeat(Math.min(password.length, 12)) + ` (${password.length} chars)` : '(empty)');
  // console.log('Policy       :', p);
  // console.log('--- Results ---');
  // console.log('Valid        :', isValid ? '✅ YES' : '❌ NO');
  if (requirementViolations.length > 0) console.log('Missing Req  :', requirementViolations.join(', '));
  console.groupEnd();

  const errorMessage = isValid ? null : `Password must include: ${requirementViolations.join(", ")}.`;

  const activeRequirements = requirements.filter(r => r.active);
  const metCount = activeRequirements.filter(r => r.met).length;
  const score = activeRequirements.length > 0 ? (metCount / activeRequirements.length) * 100 : 100;

  return {
    score,
    strength: getStrengthLabel(score),
    requirements: activeRequirements,
    isValid,
    errorMessage,
  };
};

const getStrengthLabel = (score) => {
  if (score === 0) return "Very Weak";
  if (score <= 25) return "Weak";
  if (score <= 50) return "Fair";
  if (score <= 75) return "Good";
  return "Strong";
};

export const strengthColors = {
  "Very Weak": "bg-red-500",
  "Weak": "bg-orange-500",
  "Fair": "bg-yellow-500",
  "Good": "bg-blue-500",
  "Strong": "bg-green-500",
};
